/* RESPONSIBLE TEAM: team-ai-agent */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type IntlService from 'ember-intl/services/intl';
// @ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';

export function and(...filters: any) {
  filters = filters.compact();
  if (filters.length === 1) {
    return filters[0];
  }

  return {
    type: 'and',
    filters: filters.flatMap((f: any) => (f.type === 'and' ? f.filters : f)),
  };
}

export function not(filter: any) {
  filter = copy(filter, true);
  if (filter.type === 'category') {
    let values = filter.data.values;
    if (values.length === 1 && typeof values.firstObject === 'boolean') {
      filter.data.values = [!values.firstObject];
    } else {
      filter.type = 'not_in_category';
    }
  } else if (filter.type === 'exists') {
    filter.type = 'not_exists';
  } else {
    throw new Error(`Unsupported filter type: ${filter.type}`);
  }
  return filter;
}

const filters = {
  get chatConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Desktop'],
      },
    };
  },
  get emailConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Email'],
      },
    };
  },

  routingOutcome(outcomeIdentifier: string) {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.routing_outcome.identifier',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
        values: [outcomeIdentifier],
      },
    };
  },

  get capturedContact() {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.captured_contact',
        attribute: 'conversation.fin_sdr.captured_contact',
        values: [true],
      },
    };
  },

  get noContact() {
    return and(
      {
        type: 'category',
        data: {
          property: 'fin_sdr.captured_contact',
          attribute: 'conversation.fin_sdr.captured_contact',
          values: [false],
        },
      },
      {
        type: 'category',
        data: {
          property: 'fin_sdr.had_prior_contact',
          attribute: 'conversation.fin_sdr.had_prior_contact',
          values: [false],
        },
      },
    );
  },

  get disengaged() {
    return {
      type: 'not_exists',
      data: {
        property: 'fin_sdr.routing_outcome.identifier',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
      },
    };
  },
};

export function wrap(filters: any) {
  if (filters && filters.type !== 'and' && filters.type !== 'or') {
    return {
      type: 'and',
      filters: [filters],
    };
  }
  return filters;
}

export function funnelSeries(filters?: any, label?: string) {
  return {
    label,
    metric_id: 'v1.new_conversations',
    filters: wrap(filters),
  };
}

function config(name: string, filters?: any) {
  return { name, series: funnelSeries(filters, name) };
}

export const SdrQualificationFunnelConfig = {
  get filters() {
    return filters;
  },

  buildChartSeriesFilters(routingOutcomes: Array<{ identifier: string; title: string }>) {
    let seriesFilters = [
      config('conversations'),
      config('Chat', this.filters.chatConversations),
      config('Email', this.filters.emailConversations),
    ];

    routingOutcomes.forEach((outcome) => {
      seriesFilters.push(
        config(outcome.identifier, this.filters.routingOutcome(outcome.identifier)),
      );
    });

    routingOutcomes.forEach((outcome) => {
      seriesFilters.push(
        config(
          `ChatAnd${outcome.identifier}`,
          and(this.filters.chatConversations, this.filters.routingOutcome(outcome.identifier)),
        ),
        config(
          `EmailAnd${outcome.identifier}`,
          and(this.filters.emailConversations, this.filters.routingOutcome(outcome.identifier)),
        ),
      );
    });

    routingOutcomes.forEach((outcome) => {
      seriesFilters.push(
        config(
          `${outcome.identifier}AndCapturedContact`,
          and(this.filters.routingOutcome(outcome.identifier), this.filters.capturedContact),
        ),
        config(
          `${outcome.identifier}AndNoContact`,
          and(this.filters.routingOutcome(outcome.identifier), this.filters.noContact),
        ),
      );
    });

    seriesFilters.push(
      config('CapturedContact', this.filters.capturedContact),
      config('NoContact', this.filters.noContact),
      config('Disengaged', this.filters.disengaged),
    );

    seriesFilters.push(
      config('ChatAndDisengaged', and(this.filters.chatConversations, this.filters.disengaged)),
      config('EmailAndDisengaged', and(this.filters.emailConversations, this.filters.disengaged)),
    );

    return seriesFilters;
  },

  getChartSeriesWithLabels(
    intl: IntlService,
    routingOutcomes: Array<{ identifier: string; title: string }>,
  ) {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    return seriesFilters.map((config) => ({
      ...config.series,
      label: intl.t(`ai-agent.analyze.sales-performance.funnel-metrics.${config.name}`, {
        default: config.name,
      }),
    }));
  },

  getMetricsFromChartDataRequest(
    chartData: any,
    routingOutcomes: Array<{ identifier: string; title: string }>,
  ) {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    let result: any = {};
    for (let i = 0; i < chartData.length; i++) {
      let seriesName = seriesFilters[i].name;
      let seriesData = chartData[i].data[0];
      result[seriesName] = seriesData;
    }
    return result;
  },
};
